import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * Composable for role-based authorization
 * Extracts roles from the access token and provides role checks
 */
export function useRole() {
  const authStore = useAuthStore()

  /**
   * Decode JWT token to extract claims
   */
  const decodeToken = (token: string): any => {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Failed to decode token:', error)
      return null
    }
  }

  /**
   * Get all roles from the access token
   */
  const roles = computed<string[]>(() => {
    const accessToken = authStore.accessToken
    if (!accessToken) return []

    const tokenClaims = decodeToken(accessToken)
    if (tokenClaims?.roles && Array.isArray(tokenClaims.roles)) {
      return tokenClaims.roles
    }

    return []
  })

  /**
   * Check if the current user has the Admin role
   */
  const isAdmin = computed<boolean>(() => {
    return roles.value.some(role => 
      role.toLowerCase() === 'admin'
    )
  })

  /**
   * Check if the current user has a specific role
   * @param roleName - The role name to check (case-insensitive)
   */
  const hasRole = (roleName: string): boolean => {
    return roles.value.some(role => 
      role.toLowerCase() === roleName.toLowerCase()
    )
  }

  return {
    roles,
    isAdmin,
    hasRole
  }
}
